import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import { useCallback, useEffect, useState } from 'react';
import { personalizedPromoCodeApi } from '@/apis/promoCode/personalizedPromoCodeApi';
import useCookieClient from "@hooks/useCookieClient";
import { useSession } from "@/_MiniApps/auth/AuthContext";
import { trackEvents } from '@/common/events/events';
import { createShopApplyUrl, formatPromoEventData, shouldClearPromoOnRoute, isConversionRoute, isOnPromoPost, isPromoDataValid } from '../utils/promoUtils';
import { PROMO_STORAGE_KEY, TEMP_PROMO_FOR_TRACKING_KEY } from '../utils/promoConstants';
import { unbxdAnalytics } from "@/redux/actions/unbxdActions";
import useHelpers from "@hooks/useHelpers";
import { usePromoDisplay } from "@/_MiniApps/customized_promo_codes/hooks/usePromoDisplay";
/**
 * Enhanced Promo System Hook
 * 
 * This comprehensive hook consolidates all promo-related functionality including:
 * - Drop-off tracking and detection
 * - Promo code caching and management
 * - Eligibility determination
 * - UI state management
 * - Analytics and tracking
 * - Authentication integration
 */
export var useEnhancedPromoSystem = function useEnhancedPromoSystem() {
  var _useCookieClient = useCookieClient(),
    getCookieClient = _useCookieClient.getCookieClient;
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn,
    user = _useSession.user;
  var options = {
    uuId: getCookieClient("UUID"),
    authUser: isLoggedIn && user ? user : null
  };
  var uuId = options === null || options === void 0 ? void 0 : options.uuId;
  var authUser = (options === null || options === void 0 ? void 0 : options.authUser) || null;

  // Core state management
  var _useState = useState(function () {
      try {
        var stored = localStorage.getItem(PROMO_STORAGE_KEY);
        if (stored) {
          var parsed = JSON.parse(stored);
          return parsed;
        }
      } catch (error) {
        console.error('Error loading drop-off state:', error);
      }
      var defaultState = {
        hasDroppedOff: false,
        dropOffTime: null,
        lastPath: null,
        currentPath: null,
        promocode_visible: false,
        closed_at: null,
        promoCodeData: null,
        promoCodeExpiry: null,
        post_id: null,
        first_time_visible: true // Default to true for first time
      };
      return defaultState;
    }),
    dropOffState = _useState[0],
    setDropOffState = _useState[1];
  var clearPromoCode = function clearPromoCode() {
    localStorage.removeItem(PROMO_STORAGE_KEY);
  };
  var setPromoCodeDataForTracking = function setPromoCodeDataForTracking() {
    try {
      localStorage.setItem(TEMP_PROMO_FOR_TRACKING_KEY, JSON.stringify(promoCode));
    } catch (error) {}
  };
  var getPromoCodeDataForTracking = function getPromoCodeDataForTracking() {
    try {
      var data = JSON.parse(localStorage.getItem(TEMP_PROMO_FOR_TRACKING_KEY));
      return data;
    } catch (error) {}
    return null;
  };
  var _useHelpers = useHelpers(),
    createUrl = _useHelpers.createUrl;
  var _usePromoDisplay = usePromoDisplay(),
    getDisplayConfiguration = _usePromoDisplay.getDisplayConfiguration;

  /**
   * Track promo code interaction using trackEvents method
   */
  var trackPromoCodeInteraction = useCallback(function (action, customPromoCode) {
    try {
      if (!promoCode && !customPromoCode) return;
      var eventData = customPromoCode ? formatPromoEventData(customPromoCode) : formatPromoEventData(promoCode);

      // Track different events based on action
      switch (action) {
        case 'view':
          trackEvents('ppc_banner_shown', eventData);
          unbxdAnalytics(_objectSpread({
            action: 'ppc_banner_shown'
          }, eventData));
          break;
        case 'close':
          trackEvents('ppc_banner_closed', eventData);
          unbxdAnalytics(_objectSpread({
            action: 'ppc_banner_closed'
          }, eventData));
          break;
      }
    } catch (error) {
      console.error('Error tracking promo code interaction:', error);
      // Don't throw error for tracking failures
    }
  }, [promoCode, authUser === null || authUser === void 0 ? void 0 : authUser.id, uuId]);
  var getPromoEventParams = function getPromoEventParams() {
    var nullValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    var tempPromoCodeData = getPromoCodeDataForTracking();
    if (!(promoCode !== null && promoCode !== void 0 && promoCode.code) && !(tempPromoCodeData !== null && tempPromoCodeData !== void 0 && tempPromoCodeData.code) || nullValue) {
      return {
        ppc_code: "null",
        ppc_type: "null",
        ppc_discount_amount: 0,
        ppc_warranty_duration: "null"
      };
    }
    var activePromo = promoCode ? promoCode : tempPromoCodeData; // Use first promo code
    return {
      'ppc_code': activePromo.code || null,
      ppc_type: (activePromo === null || activePromo === void 0 ? void 0 : activePromo.promo_type) || 'discount',
      ppc_discount_amount: (activePromo === null || activePromo === void 0 ? void 0 : activePromo.discount_amount) || 0,
      ppc_warranty_duration: (activePromo === null || activePromo === void 0 ? void 0 : activePromo.warranty_duration) || 0
    };
  };
  var _useState2 = useState(dropOffState.promoCodeData),
    promoCode = _useState2[0],
    setPromoCode = _useState2[1];
  var _useState3 = useState(null),
    error = _useState3[0],
    setError = _useState3[1];
  var _useState4 = useState(false),
    isPopupOpen = _useState4[0],
    setIsPopupOpen = _useState4[1];
  var _useState5 = useState({
      isEligible: false,
      lastChecked: new Date()
    }),
    eligibilityState = _useState5[0],
    setEligibilityState = _useState5[1];

  // Helper function to save state to localStorage with debouncing
  var saveState = useCallback(function (newState) {
    localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(newState));
    setDropOffState(newState);
  }, []);

  // isConversionRoute is now imported from utils

  // Check if cached promo data is valid and clear if expired
  var isCachedDataValid = useCallback(function (cachedData, cachedExpiry) {
    var isValid = isPromoDataValid(cachedData, cachedExpiry);

    // If expired, clear the drop-off state
    if (!isValid && cachedData) {
      var clearedState = {
        hasDroppedOff: false,
        dropOffTime: null,
        lastPath: dropOffState.lastPath,
        currentPath: dropOffState.currentPath,
        promocode_visible: false,
        // Clear when expired
        closed_at: null,
        promoCodeData: null,
        promoCodeExpiry: null,
        post_id: null,
        first_time_visible: true // Reset for next promo
      };
      saveState(clearedState);
      setPromoCode(null);
    }
    return isValid;
  }, [dropOffState.lastPath, dropOffState.currentPath, saveState]);

  // Fetch promo code from API with options
  var fetchPromoCode = useCallback(/*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(options) {
      var _ref2, _ref2$forceRefresh, forceRefresh, userIdForApi, response, _response$data2, _response$data, _response$data$closed, existDropOffState, updatedState, errorMsg, _errorMsg, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            _ref2 = options || {}, _ref2$forceRefresh = _ref2.forceRefresh, forceRefresh = _ref2$forceRefresh === void 0 ? false : _ref2$forceRefresh; // Check if we have valid cached data first (unless force refresh)
            if (!(!forceRefresh && isCachedDataValid(dropOffState.promoCodeData, dropOffState.promoCodeExpiry))) {
              _context.n = 1;
              break;
            }
            setPromoCode(dropOffState.promoCodeData);
            return _context.a(2);
          case 1:
            setError(null);
            _context.p = 2;
            // Determine user ID if logged in
            userIdForApi = authUser !== null && authUser !== void 0 && authUser.id ? authUser.id.toString() : undefined;
            _context.n = 3;
            return personalizedPromoCodeApi.api__getPersonalizedPromoCode(uuId,
            // uid
            userIdForApi // userId
            );
          case 3:
            response = _context.v;
            if (!response.success) {
              _context.n = 4;
              break;
            }
            if (response.data) {
              setPromoCode(response.data);
              // Update localStorage with new promo data and post_id from API
              existDropOffState = JSON.parse(localStorage.getItem(PROMO_STORAGE_KEY)) || dropOffState;
              updatedState = _objectSpread(_objectSpread({}, existDropOffState), {}, {
                closed_at: ((_response$data = response.data) === null || _response$data === void 0 ? void 0 : (_response$data$closed = _response$data.closed_at) === null || _response$data$closed === void 0 ? void 0 : _response$data$closed.toString()) || null,
                promoCodeData: response.data,
                promoCodeExpiry: response.data.expires_at.toISOString(),
                post_id: response.data.post_id,
                // Store post_id from API
                hasDroppedOff: forceRefresh ? true : existDropOffState.hasDroppedOff,
                // Keep existing state unless forced
                promocode_visible: forceRefresh ? true : existDropOffState.promocode_visible,
                // Make visible if force refresh
                first_time_visible: forceRefresh ? false : existDropOffState.first_time_visible // Set to false after fetching promo
              });
              saveState(updatedState);
            } else {
              resetPromoVisibility();
              setPromoCode(null);
            }
            return _context.a(2, (_response$data2 = response.data) !== null && _response$data2 !== void 0 ? _response$data2 : null);
          case 4:
            resetPromoVisibility();
            errorMsg = response.error || 'No promo code available';
            setError(errorMsg);
            setPromoCode(null);
          case 5:
            _context.n = 7;
            break;
          case 6:
            _context.p = 6;
            _t = _context.v;
            _errorMsg = 'An unexpected error occurred';
            setError(_errorMsg);
            setPromoCode(null);
          case 7:
            return _context.a(2);
        }
      }, _callee, null, [[2, 6]]);
    }));
    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [dropOffState, uuId, authUser, saveState, isCachedDataValid]);

  // isOnPromoPost is now imported from utils

  // Calculate eligibility for production use (no reason text for performance)
  var calculateEligibility = useCallback(function (currentPath) {
    var isEligible = false;
    var prevPath = dropOffState.lastPath || '';
    var wasOnMatchingPost = isOnPromoPost(prevPath, dropOffState.post_id);
    var isPromoVisible = dropOffState.promocode_visible;
    var hasDroppedOff = dropOffState.hasDroppedOff;
    var isExpired = promoCode ? new Date() >= promoCode.expires_at : false;

    // Check conditions in order of priority (production optimized - no reason text)
    if (!error && promoCode && !isExpired) {
      if (isPromoVisible && hasDroppedOff) {
        // Show promo if visible from drop-off (works on all pages)
        isEligible = true;
      } else if (wasOnMatchingPost) {
        // Show promo if on matching post, regardless of drop-off status
        isEligible = true;
      }
    }
    var newEligibilityState = {
      isEligible: isEligible,
      lastChecked: new Date()
    };
    setEligibilityState(newEligibilityState);
    return newEligibilityState;
  }, [dropOffState, promoCode, error]);

  // Track navigation changes for drop-off detection
  var trackNavigation = useCallback(function (currentPath) {
    var _dropOffState$current;
    console.log("track navigation", promoCode);
    setPromoCodeDataForTracking();
    // Check if current path should clear promo (success pages)
    if (shouldClearPromoOnRoute(currentPath)) {
      clearPromoCode();
      var clearedState = {
        hasDroppedOff: false,
        dropOffTime: null,
        lastPath: null,
        currentPath: currentPath,
        promocode_visible: false,
        closed_at: null,
        promoCodeData: null,
        promoCodeExpiry: null,
        post_id: null,
        first_time_visible: true
      };
      saveState(clearedState);
      setPromoCode(null);
      return;
    }

    // Use stored lastPath from localStorage as primary source
    var previousPath = (_dropOffState$current = dropOffState.currentPath) !== null && _dropOffState$current !== void 0 ? _dropOffState$current : dropOffState.lastPath;
    var storedPostId = dropOffState.post_id; // Post ID from API
    var wasOnPromoPostPage = isOnPromoPost(previousPath, storedPostId);
    // Check for drop-off detection - if user has promo stored and leaves any conversion flow
    var wasInConversion = previousPath ? isConversionRoute(previousPath) : false;
    var isNowInConversion = isConversionRoute(currentPath);
    if ((wasInConversion || wasOnPromoPostPage) && !isNowInConversion) {
      var newState = _objectSpread(_objectSpread({}, dropOffState), {}, {
        hasDroppedOff: true,
        dropOffTime: Date.now(),
        lastPath: dropOffState.currentPath,
        currentPath: currentPath,
        promocode_visible: true,
        // Make promo visible after drop-off
        first_time_visible: dropOffState.first_time_visible // Keep existing value
      });
      saveState(newState);
    } else {
      // Just update the last path, preserve other states
      var _newState = _objectSpread(_objectSpread({}, dropOffState), {}, {
        lastPath: dropOffState.currentPath,
        currentPath: currentPath
      });
      if (JSON.stringify(_newState) !== JSON.stringify(dropOffState)) {
        saveState(_newState);
      }
    }

    // Recalculate eligibility for current page
    calculateEligibility(currentPath);
  }, [dropOffState, saveState]);

  // Handle promo shown event - keep promocode_visible true until expiry
  var handlePromoShown = useCallback(function () {
    fetchPromoCode({
      forceRefresh: true
    }).then(function (promoCode) {
      trackPromoCodeInteraction('view', promoCode);
    })["catch"](function (err) {});
  }, [trackPromoCodeInteraction]);

  // Handle promo closed event - keep promocode_visible true, only set closed_at
  var handlePromoClosed = useCallback(function () {
    var newState = _objectSpread(_objectSpread({}, dropOffState), {}, {
      closed_at: new Date().toISOString()
    });
    saveState(newState);
    setIsPopupOpen(false);
    trackPromoCodeInteraction('close');
  }, [dropOffState, saveState, trackPromoCodeInteraction]);

  // Handle book now action - redirect to shop-apply and auto-apply code
  var handleBookNow = useCallback(function () {
    if (!promoCode) return;
    // Navigate to shop-apply page with post_id and auto-apply promo code
    window.location.href = createUrl(createShopApplyUrl(promoCode.post_id, promoCode.code));
    var newState = _objectSpread(_objectSpread({}, dropOffState), {}, {
      closed_at: new Date().toISOString()
    });
    saveState(newState);
    setIsPopupOpen(false);
    // Don't call handlePromoShown - keep promo visible until expiry
  }, [promoCode]);

  // Handle button click - redirect to shop-apply instead of opening popup
  var handleButtonClick = useCallback(function () {
    if (!promoCode) return;

    // Navigate to shop-apply page with post_id and auto-apply promo code
    window.location.href = createUrl(createShopApplyUrl(promoCode.post_id, promoCode.code));
    // Don't call handlePromoShown - keep promo visible until expiry
  }, [promoCode]);

  // Mark first display as complete (used by delay hook)
  var markFirstDisplayComplete = useCallback(function () {
    var updatedState = _objectSpread(_objectSpread({}, dropOffState), {}, {
      first_time_visible: false
    });
    saveState(updatedState);
  }, [dropOffState, saveState]);

  // Reset promo visibility (for testing)
  var resetPromoVisibility = useCallback(function () {
    var currentPath = window.location.pathname;
    var newState = {
      hasDroppedOff: false,
      dropOffTime: null,
      lastPath: currentPath,
      currentPath: currentPath,
      promocode_visible: false,
      closed_at: null,
      promoCodeData: null,
      promoCodeExpiry: null,
      post_id: null,
      first_time_visible: true
    };
    saveState(newState);
    setPromoCode(null);
    setIsPopupOpen(false);
  }, [dropOffState.lastPath, saveState]);

  // Initialize promo code fetch when auth is ready
  useEffect(function () {
    fetchPromoCode();
  }, []);

  // Update promo code state when drop-off state changes
  useEffect(function () {
    if (dropOffState.promoCodeData && !promoCode) {
      setPromoCode(dropOffState.promoCodeData);
    }
  }, [dropOffState.promoCodeData, promoCode]);

  // Prepare popup for auto-open when drop-off conditions are met (delay logic handled by GlobalPromoManager)
  useEffect(function () {
    var isPromoVisible = dropOffState.promocode_visible;
    var hasDroppedOff = dropOffState.hasDroppedOff;
    var shouldAutoOpenPopup = isPromoVisible && hasDroppedOff && promoCode && !isExpired && !dropOffState.closed_at;
    if (shouldAutoOpenPopup && !isPopupOpen) {
      // Check if we're on the promo post page (should show button, not popup)
      var currentPath = window.location.pathname;
      var isOnPromoPostPage = promoCode && isOnPromoPost(currentPath, promoCode.post_id);

      // Only prepare popup if NOT on promo post page
      // Actual opening is controlled by GlobalPromoManager with delay logic
      if (!isOnPromoPostPage) {
        setIsPopupOpen(true);
      }
    }
  }, [dropOffState.promocode_visible, dropOffState.hasDroppedOff, dropOffState.closed_at, promoCode, isExpired, isPopupOpen]);

  // Calculate initial eligibility
  useEffect(function () {
    calculateEligibility();
  }, [promoCode, dropOffState.promocode_visible, dropOffState.closed_at]);
  useEffect(function () {
    if (dropOffState.promoCodeData && dropOffState.promoCodeExpiry) {
      isCachedDataValid(dropOffState.promoCodeData, dropOffState.promoCodeExpiry);
    }
  }, [dropOffState.promoCodeData, dropOffState.promoCodeExpiry, isCachedDataValid]);
  var isExpired = promoCode ? new Date() >= promoCode.expires_at : false;
  var shouldShowBanner = eligibilityState.isEligible && !isExpired;
  // Get display configuration using the reusable hook
  var displayConfig = getDisplayConfiguration(promoCode, {
    hasDroppedOff: dropOffState.hasDroppedOff,
    promocode_visible: dropOffState.promocode_visible,
    closed_at: dropOffState.closed_at
  });
  return {
    // State
    promoCode: promoCode,
    error: error,
    isExpired: isExpired,
    eligibilityState: eligibilityState,
    dropOffState: dropOffState,
    // UI State
    isPopupOpen: isPopupOpen,
    setIsPopupOpen: setIsPopupOpen,
    shouldShowBanner: shouldShowBanner,
    // Actions
    handlePromoShown: handlePromoShown,
    handlePromoClosed: handlePromoClosed,
    handleBookNow: handleBookNow,
    handleButtonClick: handleButtonClick,
    resetPromoVisibility: resetPromoVisibility,
    markFirstDisplayComplete: markFirstDisplayComplete,
    // Navigation tracking
    trackNavigation: trackNavigation,
    trackPromoCodeInteraction: trackPromoCodeInteraction,
    getPromoEventParams: getPromoEventParams,
    displayConfig: displayConfig,
    getPromoCodeDataForTracking: getPromoCodeDataForTracking
  };
};